// src/pages/QuoteBuilder/index.tsx
/* eslint-disable no-console */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Typography, Alert } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import AppHeader from '../../components/AppHeader';
import logo from '../../components/logo.svg';
import { quotesAPI } from '../../services/api';

import { VMRow, PaasDatabaseRow, VmDatabaseRow, DatabaseRow, OtherLineItem, PowerVMRow, PowerAddonLineItem, Totals, SelectOption } from './types';
import { useQuoteData } from './useQuoteData';
import { usePriceCalculation } from './usePriceCalculation';
import { useAvailability } from './useAvailability';
import { generatePDF } from './exportPdf';
import QuoteHeader from './QuoteHeader';
import VMsTable from './VMsTable';
import VMConfigSection from './VMConfigSection';
import DatabasesTable from './DatabasesTable';
import OtherItemsTable from './OtherItemsTable';
import PowerVMsTable from './PowerVMsTable';
import PowerAddonsTable from './PowerAddonsTable';

/* ---------------- DEV-ONLY: suppress noisy MUI warnings ---------------- */
if (process.env.NODE_ENV !== 'production') {
  const originalWarn = console.warn;
  console.warn = (...args: unknown[]) => {
    const msg = args?.[0];
    if (typeof msg === 'string' &&
      (msg.includes('out-of-range value to the select') ||
       msg.includes('value provided to the Select input is invalid') ||
       msg.includes('uncontrolled'))) {
      return;
    }
    originalWarn(...args);
  };
}

const QuoteBuilder = () => {
  const { user } = useAuth();

  const data = useQuoteData();
  const {
    providers, regions, environments, vmTypes, osTypes, disks,
    databaseTypes, databaseConfigs, databaseEditions, addons, addonOptions,
    vmPricing, diskPricing, addonPricing, databasePricing, databaseVMPricing, addonOptionPricing,
    defaultAddons,
    powerTypes, powerAddons, powerAddonOptions,
    powerVMPricing, powerAddonPricing, powerAddonOptionPricing, powerDefaultAddons, powerDiskPricing,
    backupPricing, databaseBackupPricing, databaseExtraStoragePricing,
    drTestingPricing,
    filteredVMTypes, filteredOSTypes, filteredDisks, filteredPowerDisks,
    filteredDatabaseTypes, filteredDatabaseConfigs, filteredAddons,
    selectedProvider, setSelectedProvider, selectedRegion, setSelectedRegion,
    loading, error, setError, pricingReady, isLoadingQuote, setIsLoadingQuote, setPricingReady,
    hasLoadedOnce, desiredRegionIdRef,
    quoteIdFromRouteOrQuery,
  } = data;

  const pricing = usePriceCalculation({
    vmTypes, osTypes, environments, filteredVMTypes, disks,
    vmPricing, diskPricing, addonPricing, databasePricing, databaseVMPricing, addonOptionPricing,
    defaultAddons, selectedRegion,
    powerVMPricing, powerAddonPricing, powerAddonOptionPricing, powerDefaultAddons, powerDiskPricing,
    backupPricing, databaseBackupPricing, databaseExtraStoragePricing,
    drTestingPricing,
  });

  const {
    getVMPrice, getDiskPrice, getDiskSizeGB, getDatabasePrice, getDatabaseTotalPrice, getDatabaseVMPrice,
    getAddonCostByIds, getAvailableRAM, getAvailableRAMForDatabase,
    getVMTypeId, calculateVMCost, getOSCategoryById,
    calculatePowerVMCost, getPowerAddonCost, getPowerAddonCostByIds, getPowerDiskPrice,
    getBackupCostPerGB, getDatabaseBackupCostPerGB, getDatabaseExtraStorageCostPerGB,
    getDRTestingCost,
  } = pricing;

  const availability = useAvailability({
    vmPricing, diskPricing, addonPricing, addonOptionPricing,
    databasePricing, databaseVMPricing,
    selectedRegion, pricingReady,
    getVMTypeId, getOSCategoryById,
    powerVMPricing, powerAddonPricing, powerAddonOptionPricing,
  });

  const {
    isVMRowAvailable, isPaasDbRowAvailable, isVmDbRowAvailable,
    isAddonRowAvailable, ensureCurrentOption,
    isPowerVMRowAvailable, isPowerAddonRowAvailable,
  } = availability;

  /* -------- Quote line items state -------- */
  const [quoteName, setQuoteName] = useState('');
  const [vms, setVMs] = useState<VMRow[]>([]);
  const [databases, setDatabases] = useState<DatabaseRow[]>([]);
  const [otherLineItems, setOtherLineItems] = useState<OtherLineItem[]>([]);
  const [powerVMs, setPowerVMs] = useState<PowerVMRow[]>([]);
  const [powerAddonItems, setPowerAddonItems] = useState<PowerAddonLineItem[]>([]);
  const [savedTotals, setSavedTotals] = useState<Totals | null>(null);
  const [bannerMsg, setBannerMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loadedQuoteId, setLoadedQuoteId] = useState<number | null>(null);
  const [loadedQuoteName, setLoadedQuoteName] = useState('');

  const isIBM = providers.find(p => p.ProviderId === parseInt(String(selectedProvider), 10))?.ProviderName?.toLowerCase().includes('ibm') ?? false;

  const standardEnvironments = useMemo(
    () => environments.filter(e => e.EnabledForStandardVMs !== 0),
    [environments]
  );
  const ibmPowerEnvironments = useMemo(
    () => environments.filter(e => e.EnabledForIBMPower !== 0),
    [environments]
  );

  /* ---------------- Provider/Region change: banner ---------------- */
  useEffect(() => {
    if (!selectedProvider || !selectedRegion) return;
    if (!hasLoadedOnce.current) return;
    if (isLoadingQuote) return;

    let unavailableCount = 0;
    vms.forEach((vm) => { if (!isVMRowAvailable(vm)) unavailableCount += 1; });
    databases.forEach((db) => {
      if (db.deploymentType === 'vm_based') { if (!isVmDbRowAvailable(db)) unavailableCount += 1; }
      else { if (!isPaasDbRowAvailable(db)) unavailableCount += 1; }
    });
    otherLineItems.forEach((item) => { if (!isAddonRowAvailable(item)) unavailableCount += 1; });
    powerVMs.forEach((pvm) => { if (!isPowerVMRowAvailable(pvm)) unavailableCount += 1; });
    powerAddonItems.forEach((pai) => { if (!isPowerAddonRowAvailable(pai)) unavailableCount += 1; });

    setBannerMsg(unavailableCount > 0
      ? `${unavailableCount} item${unavailableCount > 1 ? 's are' : ' is'} not available in this provider/region and excluded from totals.`
      : ''
    );
  }, [selectedProvider, selectedRegion, pricingReady, isLoadingQuote, vms, databases, otherLineItems, powerVMs, powerAddonItems]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------------- Load quote ---------------- */
  const hydrateFromQuote = useCallback(async (q: Record<string, unknown>) => {
    try {
      setIsLoadingQuote(true);
      setPricingReady(false);
      if (q.providerId) setSelectedProvider(q.providerId as number);
      if (q.regionId) { desiredRegionIdRef.current = q.regionId as number; setSelectedRegion(q.regionId as number); }
      if (q.quoteName) setQuoteName(q.quoteName as string);

      if (q.quoteId) setLoadedQuoteId(q.quoteId as number);
      if (q.quoteName) setLoadedQuoteName(q.quoteName as string);

      setSavedTotals({ monthly: (q.totalMonthlyCost as number) ?? 0, annual: (q.totalAnnualCost as number) ?? 0, oneTime: 0 });

      // VMs
      const vmLines: VMRow[] = ((q.vms as Record<string, unknown>[]) ?? []).map((line) => {
        const vmDef = vmTypes.find((v) => v.VMTypeId === parseInt(String(line.vmTypeId), 10));
        return {
          id: Date.now() + Math.random(),
          name: (line.name as string) ?? 'Virtual Machine',
          cpu: vmDef?.CPU ?? 2,
          ram: vmDef?.RAM ?? 4,
          osTypeId: parseInt(String(line.osTypeId), 10) || '',
          environmentId: parseInt(String(line.environmentId), 10) || 1,
          disks: ((line.disks as unknown[]) ?? []).map((d) => parseInt(String((d as Record<string, unknown>)?.diskId ?? (d as Record<string, unknown>)?.DiskId ?? d), 10)).filter(Boolean),
          quantity: parseInt(String(line.quantity), 10) || 1,
          dailyBackups: parseInt(String(line.backups), 10) || 0,
          drTestingFrequency: parseInt(String(line.drTestingFrequency), 10) || 0,
          savedLineCost: (line.lineTotalMonthlyCost as number) ?? null,
        };
      });

      // PaaS DBs
      const paasLines: PaasDatabaseRow[] = ((q.databases as Record<string, unknown>[]) ?? []).map((line) => {
        const cfg = databaseConfigs.find((dc) => dc.DatabaseConfigId === parseInt(String(line.databaseConfigId), 10));
        const typeId = cfg?.DatabaseTypeId ?? databaseTypes[0]?.DatabaseTypeId ?? '';
        return {
          id: Date.now() + Math.random(),
          databaseTypeId: typeId,
          deploymentType: 'paas' as const,
          cores: cfg?.Cores ?? 2,
          tier: cfg?.Tier ?? '',
          databaseConfigId: cfg?.DatabaseConfigId ?? '',
          quantity: parseInt(String(line.quantity), 10) || 1,
          dailyBackups: parseInt(String(line.backups), 10) || 0,
          extraStorageGB: parseInt(String(line.extraStorageGB), 10) || 0,
          savedLineCost: (line.lineTotalMonthlyCost as number) ?? null,
        };
      });

      // VM-based DBs
      const vmDbLines: VmDatabaseRow[] = ((q.databaseVMs as Record<string, unknown>[]) ?? []).map((line) => {
        const vmDef = vmTypes.find((v) => v.VMTypeId === parseInt(String(line.vmTypeId), 10));
        const editionId = parseInt(String(line.databaseEditionId), 10) || '';
        const edition = databaseEditions.find((e) => e.DatabaseEditionId === editionId);
        const dbTypeId = edition?.DatabaseTypeId ?? (databaseTypes.find((t) => t.DeploymentType === 'vm_based')?.DatabaseTypeId ?? '');
        return {
          id: Date.now() + Math.random(),
          databaseTypeId: dbTypeId,
          deploymentType: 'vm_based' as const,
          cpu: vmDef?.CPU ?? 2,
          ram: vmDef?.RAM ?? 4,
          editionId,
          environmentId: parseInt(String(line.environmentId), 10) || 1,
          disks: ((line.disks as unknown[]) ?? []).map((d) => parseInt(String((d as Record<string, unknown>)?.diskId ?? (d as Record<string, unknown>)?.DiskId ?? d), 10)).filter(Boolean),
          quantity: parseInt(String(line.quantity), 10) || 1,
          dailyBackups: parseInt(String(line.backups), 10) || 0,
          savedLineCost: (line.lineTotalMonthlyCost as number) ?? null,
        };
      });

      // Add-ons
      const addonLines: OtherLineItem[] = ((q.addons as Record<string, unknown>[]) ?? []).map((line) => ({
        id: Date.now() + Math.random(),
        itemId: parseInt(String(line.addonId), 10) || '',
        optionId: line.addonOptionId ? parseInt(String(line.addonOptionId), 10) : '',
        quantity: parseInt(String(line.quantity), 10) || 1,
        savedLineCost: (line.lineTotalMonthlyCost as number) ?? null,
      }));

      // Power VMs — snap RAM to nearest 4 GB multiple (minimum 4) to match updated options
      const powerVMLines: PowerVMRow[] = ((q.powerVMs as Record<string, unknown>[]) ?? []).map((line) => {
        const rawRam = Number(line.ram) || 4;
        const snappedRam = Math.max(4, Math.round(rawRam / 4) * 4);
        return {
          id: Date.now() + Math.random(),
          name: (line.name as string) ?? '',
          powerTypeId: parseInt(String(line.powerTypeId), 10) || '',
          environmentId: parseInt(String(line.environmentId), 10) || 1,
          cpu: Number(line.cpu) || 0.25,
          ram: snappedRam,
          quantity: parseInt(String(line.quantity), 10) || 1,
          disks: ((line.disks as unknown[]) ?? []).map((d) => parseInt(String((d as Record<string, unknown>)?.diskId ?? d), 10)).filter(Boolean),
          savedLineCost: (line.lineTotalMonthlyCost as number) ?? null,
        };
      });

      // Power Addons
      const powerAddonLines: PowerAddonLineItem[] = ((q.powerAddonItems as Record<string, unknown>[]) ?? []).map((line) => ({
        id: Date.now() + Math.random(),
        powerAddonId: parseInt(String(line.powerAddonId), 10) || '',
        powerAddonOptionId: line.powerAddonOptionId != null ? parseInt(String(line.powerAddonOptionId), 10) : '',
        quantity: parseInt(String(line.quantity), 10) || 1,
        savedLineCost: (line.lineTotalMonthlyCost as number) ?? null,
      }));

      setVMs(vmLines);
      setDatabases([...paasLines, ...vmDbLines]);
      setOtherLineItems(addonLines);
      setPowerVMs(powerVMLines);
      setPowerAddonItems(powerAddonLines);
    } catch (err) {
      setError('Failed to load quote contents');
      console.error('[Quote Load] Error:', err);
      setIsLoadingQuote(false);
    }
  }, [vmTypes, databaseTypes, databaseConfigs, databaseEditions, setIsLoadingQuote, setPricingReady, setSelectedProvider, setSelectedRegion, desiredRegionIdRef, setError]);

  useEffect(() => {
    const loadQuote = async () => {
      if (!quoteIdFromRouteOrQuery) return;
      try {
        const id = parseInt(quoteIdFromRouteOrQuery, 10);
        const resp = await quotesAPI.getById(id);
        if (!resp?.data) { setError('Quote not found'); return; }
        const raw = resp.data;
        const mapped = {
          quoteId: raw.quote?.QuoteId ?? id,
          quoteName: raw.quote?.QuoteName ?? '',
          providerId: raw.quote?.ProviderId ?? null,
          regionId: raw.quote?.RegionId ?? null,
          totalMonthlyCost: raw.quote?.TotalMonthlyCost ?? 0,
          totalAnnualCost: raw.quote?.TotalAnnualCost ?? 0,
          vms: raw.vms ?? [],
          databases: raw.databases ?? [],
          databaseVMs: raw.databaseVMs ?? [],
          addons: raw.addons ?? [],
          powerVMs: raw.powerVMs ?? [],
          powerAddonItems: raw.powerAddonItems ?? [],
        };
        await hydrateFromQuote(mapped);
      } catch {
        setError('Failed to load saved quote');
      }
    };
    loadQuote();
  }, [quoteIdFromRouteOrQuery, hydrateFromQuote, setError]);

  useEffect(() => {
    if (isLoadingQuote && pricingReady) {
      setIsLoadingQuote(false);
      setSavedTotals(null);
      // Clear individual savedLineCost so real availability checks run
      setVMs((prev) => prev.map((vm) => vm.savedLineCost != null ? { ...vm, savedLineCost: null } : vm));
      setDatabases((prev) => prev.map((db) => db.savedLineCost != null ? { ...db, savedLineCost: null } as typeof db : db));
      setOtherLineItems((prev) => prev.map((item) => item.savedLineCost != null ? { ...item, savedLineCost: null } : item));
      setPowerVMs((prev) => prev.map((pvm) => pvm.savedLineCost != null ? { ...pvm, savedLineCost: null } : pvm));
      setPowerAddonItems((prev) => prev.map((pai) => pai.savedLineCost != null ? { ...pai, savedLineCost: null } : pai));
    }
  }, [isLoadingQuote, pricingReady, setIsLoadingQuote]);

  /* ---------------- Totals ---------------- */
  const totals = useMemo(() => {
    if (!pricingReady && savedTotals) return savedTotals;

    let monthly = 0;

    vms.forEach((vm) => {
      if (!isVMRowAvailable(vm)) return;
      monthly += calculateVMCost(vm);
    });

    databases.forEach((db) => {
      if (db.deploymentType === 'vm_based') {
        if (!isVmDbRowAvailable(db)) return;
        const vmTypeId = getVMTypeId(db.cpu, db.ram);
        if (!vmTypeId) return;
        const unit = getDatabaseVMPrice(vmTypeId, db.editionId, selectedRegion, db.disks ?? [], db.environmentId ?? 1);
        // VM-based DB backup cost
        const totalDiskGB = (db.disks ?? []).reduce((s: number, d) => s + getDiskSizeGB(d), 0);
        const backupCostPerGB = getBackupCostPerGB(selectedRegion);
        const backupCost = totalDiskGB * backupCostPerGB * Math.max(0, (db.dailyBackups ?? 0) - 1);
        monthly += (unit + backupCost) * db.quantity;
      } else {
        if (!isPaasDbRowAvailable(db)) return;
        monthly += getDatabaseTotalPrice(db.databaseConfigId, selectedRegion, db.dailyBackups ?? 0, db.extraStorageGB ?? 0) * db.quantity;
      }
    });

    otherLineItems.forEach((item) => {
      if (!isAddonRowAvailable(item)) return;
      const addonId = parseInt(String(item.itemId), 10);
      const unit = getAddonCostByIds(addonId, item.optionId ? parseInt(String(item.optionId), 10) : null);
      monthly += unit * item.quantity;
    });

    powerVMs.forEach((pvm) => {
      if (!isPowerVMRowAvailable(pvm)) return;
      monthly += calculatePowerVMCost(pvm);
    });

    let oneTime = 0;
    powerAddonItems.forEach((pai) => {
      if (!isPowerAddonRowAvailable(pai)) return;
      const addonId = parseInt(String(pai.powerAddonId), 10);
      const optId = pai.powerAddonOptionId ? parseInt(String(pai.powerAddonOptionId), 10) : null;
      const cost = getPowerAddonCostByIds(addonId, optId) * pai.quantity;
      if (powerAddons.find((a) => a.PowerAddonId === addonId)?.IsOneTime) {
        oneTime += cost;
      } else {
        monthly += cost;
      }
    });

    return { monthly, annual: monthly * 12, oneTime };
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    vms, databases, otherLineItems, powerVMs, powerAddonItems, selectedRegion, pricingReady, savedTotals,
    vmPricing, diskPricing, databasePricing, databaseVMPricing,
    addonPricing, addonOptionPricing, defaultAddons, vmTypes, osTypes, environments,
    powerVMPricing, powerAddonPricing, powerDefaultAddons, powerAddons
  ]);

  /* ---------------- Add / Update handlers ---------------- */
  const clearSavedLineCosts = () => {
    setVMs((prev) => prev.map((vm) => vm.savedLineCost != null ? { ...vm, savedLineCost: null } : vm));
    setDatabases((prev) => prev.map((db) => db.savedLineCost != null ? { ...db, savedLineCost: null } as typeof db : db));
    setOtherLineItems((prev) => prev.map((item) => item.savedLineCost != null ? { ...item, savedLineCost: null } : item));
    setPowerVMs((prev) => prev.map((pvm) => pvm.savedLineCost != null ? { ...pvm, savedLineCost: null } : pvm));
    setPowerAddonItems((prev) => prev.map((pai) => pai.savedLineCost != null ? { ...pai, savedLineCost: null } : pai));
  };

  const clearSavedCosts = () => {
    setSavedTotals(null);
    clearSavedLineCosts();
  };

  const handleAddVM = () => {
    const firstVM = filteredVMTypes[0];
    const defaultCpu = firstVM?.CPU ?? 2;
    const defaultRam = firstVM?.RAM ?? 4;
    setVMs((prev) => ([
      ...prev,
      {
        id: Date.now(),
        name: `VM${prev.length + 1}`,
        cpu: defaultCpu,
        ram: defaultRam,
        osTypeId: filteredOSTypes[0]?.OSTypeId ?? '',
        environmentId: standardEnvironments[0]?.EnvironmentId ?? 1,
        disks: [],
        quantity: 1,
        dailyBackups: 0,
        drTestingFrequency: 0,
      },
    ]));
  };

  const handleUpdateVM = (id: number, field: string, value: unknown) => {
    clearSavedCosts();
    setVMs((prev) => prev.map((vm) => {
      if (vm.id !== id) return vm;
      const cleared = { ...vm, savedLineCost: null };
      if (field === 'cpu') {
        const cpuVal = Number(value);
        const rams = getAvailableRAM(cpuVal);
        return { ...cleared, cpu: cpuVal, ram: rams[0] ?? vm.ram };
      }
      if (field === 'ram') return { ...cleared, ram: Number(value) };
      if (field === 'osTypeId') return { ...cleared, osTypeId: Number(value) || '' };
      if (field === 'quantity') return { ...cleared, quantity: Number(value) || 1 };
      if (field === 'dailyBackups') return { ...cleared, dailyBackups: Number(value) || 0 };
      return { ...cleared, [field]: value };
    }));
  };

  const handleDeleteVM = (id: number) => { clearSavedCosts(); setVMs((prev) => prev.filter((vm) => vm.id !== id)); };

  const handleAddDisk = (vmId: number) => {
    clearSavedCosts();
    setVMs((prev) => prev.map((vm) => (vm.id !== vmId ? vm : ((vm.disks ?? []).length >= 3 ? vm : { ...vm, savedLineCost: null, disks: [...(vm.disks ?? []), filteredDisks[0]?.DiskId ?? null] }))));
  };

  const handleUpdateDisk = (vmId: number, idx: number, diskId: number | null) => {
    clearSavedCosts();
    setVMs((prev) => prev.map((vm) => {
      if (vm.id !== vmId) return vm;
      const newDisks = [...(vm.disks ?? [])];
      newDisks[idx] = diskId ?? null;
      return { ...vm, savedLineCost: null, disks: newDisks };
    }));
  };

  const handleRemoveDisk = (vmId: number, idx: number) => {
    clearSavedCosts();
    setVMs((prev) => prev.map((vm) => (vm.id !== vmId ? vm : { ...vm, savedLineCost: null, disks: (vm.disks ?? []).filter((_, i) => i !== idx) })));
  };

  const getAvailableTiers = (databaseTypeId: number | string, cores: number) =>
    filteredDatabaseConfigs.filter((dc) => dc.DatabaseTypeId === parseInt(String(databaseTypeId), 10) && dc.Cores === cores);

  const handleAddDatabase = () => {
    const firstType = filteredDatabaseTypes[0]; if (!firstType) return;
    const row: Record<string, unknown> = { id: Date.now(), databaseTypeId: firstType.DatabaseTypeId, deploymentType: firstType.DeploymentType, quantity: 1, dailyBackups: 0, extraStorageGB: 0 };
    if (firstType.DeploymentType === 'vm_based') {
      const firstVM = filteredVMTypes[0];
      const firstEdition = databaseEditions.find((e) => e.DatabaseTypeId === firstType.DatabaseTypeId);
      row.cpu = firstVM?.CPU ?? 2; row.ram = firstVM?.RAM ?? 4; row.editionId = firstEdition?.DatabaseEditionId ?? ''; row.disks = [];
      row.environmentId = standardEnvironments[0]?.EnvironmentId ?? 1;
    } else {
      const firstCfg = filteredDatabaseConfigs.find((dc) => dc.DatabaseTypeId === firstType.DatabaseTypeId);
      row.cores = firstCfg?.Cores ?? 2; row.tier = firstCfg?.Tier ?? ''; row.databaseConfigId = firstCfg?.DatabaseConfigId ?? '';
    }
    setDatabases((prev) => [...prev, row as unknown as DatabaseRow]);
  };

  const handleUpdateDatabase = (id: number, field: string, value: unknown) => {
    clearSavedCosts();
    setDatabases((prev) => prev.map((db) => {
      if (db.id !== id) return db;
      if (field === 'databaseTypeId') {
        const newTypeId = Number(value);
        const newType = databaseTypes.find((dt) => dt.DatabaseTypeId === newTypeId);
        if (newType?.DeploymentType === 'vm_based') {
          const firstVM = filteredVMTypes[0];
          const firstEdition = databaseEditions.find((e) => e.DatabaseTypeId === newTypeId);
          return {
            id: db.id, databaseTypeId: newTypeId, deploymentType: 'vm_based' as const,
            cpu: firstVM?.CPU ?? 2, ram: firstVM?.RAM ?? 4, editionId: firstEdition?.DatabaseEditionId ?? '',
            disks: [], quantity: db.quantity, dailyBackups: 0,
            environmentId: standardEnvironments[0]?.EnvironmentId ?? 1,
          };
        }
        const firstCfg = filteredDatabaseConfigs.find((dc) => dc.DatabaseTypeId === newTypeId);
        return {
          id: db.id, databaseTypeId: newTypeId, deploymentType: 'paas' as const,
          cores: firstCfg?.Cores ?? 2, tier: firstCfg?.Tier ?? '', databaseConfigId: firstCfg?.DatabaseConfigId ?? '',
          quantity: db.quantity, dailyBackups: 0, extraStorageGB: 0,
        };
      }

      const cleared = { ...db, savedLineCost: null };
      if (db.deploymentType === 'vm_based') {
        if (field === 'cpu') {
          const cpuVal = Number(value);
          const rams = getAvailableRAMForDatabase(cpuVal);
          return { ...cleared, cpu: cpuVal, ram: rams[0] ?? db.ram } as VmDatabaseRow;
        }
        if (field === 'ram') return { ...cleared, ram: Number(value) } as VmDatabaseRow;
        if (field === 'editionId') return { ...cleared, editionId: Number(value) || '' } as VmDatabaseRow;
        if (field === 'quantity') return { ...cleared, quantity: Number(value) || 1 } as VmDatabaseRow;
        if (field === 'disks') return { ...cleared, disks: value as (number | null)[] } as VmDatabaseRow;
        if (field === 'dailyBackups') return { ...cleared, dailyBackups: Number(value) || 0 } as VmDatabaseRow;
        return { ...cleared, [field]: value } as VmDatabaseRow;
      }

      // PaaS
      if (field === 'cores') {
        const coresVal = Number(value);
        const tiers = getAvailableTiers(db.databaseTypeId, coresVal);
        const firstTier = tiers[0];
        return { ...cleared, cores: coresVal, tier: firstTier?.Tier ?? '', databaseConfigId: firstTier?.DatabaseConfigId ?? '' } as PaasDatabaseRow;
      }
      if (field === 'tier') {
        const cfg = filteredDatabaseConfigs.find((dc) =>
          dc.DatabaseTypeId === parseInt(String(db.databaseTypeId), 10) && dc.Cores === (db as PaasDatabaseRow).cores && dc.Tier === value);
        return { ...cleared, tier: value as string, databaseConfigId: cfg?.DatabaseConfigId ?? '' } as PaasDatabaseRow;
      }
      if (field === 'quantity') return { ...cleared, quantity: Number(value) || 1 } as PaasDatabaseRow;
      if (field === 'dailyBackups') return { ...cleared, dailyBackups: Number(value) || 0 } as PaasDatabaseRow;
      if (field === 'extraStorageGB') return { ...cleared, extraStorageGB: Number(value) || 0 } as PaasDatabaseRow;
      return { ...cleared, [field]: value } as PaasDatabaseRow;
    }));
  };

  const handleDeleteDatabase = (id: number) => { clearSavedCosts(); setDatabases((prev) => prev.filter((db) => db.id !== id)); };

  const handleAddDatabaseDisk = (dbId: number) => {
    clearSavedCosts();
    setDatabases((prev) => prev.map((db) => {
      if (db.id !== dbId || db.deploymentType !== 'vm_based') return db;
      if ((db.disks ?? []).length >= 3) return db;
      return { ...db, savedLineCost: null, disks: [...(db.disks ?? []), filteredDisks[0]?.DiskId ?? null] };
    }));
  };

  const handleUpdateDatabaseDisk = (dbId: number, idx: number, diskId: number | null) => {
    clearSavedCosts();
    setDatabases((prev) => prev.map((db) => {
      if (db.id !== dbId || db.deploymentType !== 'vm_based') return db;
      const newDisks = [...(db.disks ?? [])]; newDisks[idx] = (diskId ?? null);
      return { ...db, savedLineCost: null, disks: newDisks };
    }));
  };

  const handleDeleteDatabaseDisk = (dbId: number, idx: number) => {
    clearSavedCosts();
    setDatabases((prev) => prev.map((db) => {
      if (db.id !== dbId || db.deploymentType !== 'vm_based') return db;
      return { ...db, savedLineCost: null, disks: (db.disks ?? []).filter((_, i) => i !== idx) };
    }));
  };

  const handleAddOtherItem = () => {
    const availableAddons = filteredAddons.filter((a) => !defaultAddons.some((da) => da.AddonId === a.AddonId));
    const firstAddon = availableAddons[0];
    const initialOptionId =
      firstAddon && addonOptions[firstAddon.AddonId]?.length
        ? addonOptions[firstAddon.AddonId]![0]?.AddonOptionId ?? ''
        : '';
    setOtherLineItems((prev) => [...prev, { id: Date.now(), itemId: firstAddon?.AddonId ?? '', optionId: initialOptionId, quantity: 1 }]);
  };

  const handleUpdateOtherItem = (id: number, field: string, value: unknown) => {
    clearSavedCosts();
    setOtherLineItems((prev) => prev.map((item) => {
      if (item.id !== id) return item;
      if (field === 'itemId') {
        const addonId = Number(value) || '';
        const addon = filteredAddons.find((a) => a.AddonId === addonId);
        if (addon && addonOptions[addon.AddonId]?.length) {
          const first = addonOptions[addon.AddonId]![0];
          return { ...item, savedLineCost: null, itemId: addonId, optionId: first?.AddonOptionId ?? '' };
        }
        return { ...item, savedLineCost: null, itemId: addonId, optionId: '' };
      }
      if (field === 'optionId') return { ...item, savedLineCost: null, optionId: Number(value) || '' };
      if (field === 'quantity') return { ...item, savedLineCost: null, quantity: Number(value) || 1 };
      return { ...item, savedLineCost: null, [field]: value };
    }));
  };

  const handleDeleteOtherItem = (id: number) => { clearSavedCosts(); setOtherLineItems((prev) => prev.filter((i) => i.id !== id)); };

  /* --- Power VM handlers --- */
  const handleAddPowerVM = () => {
    const firstType = powerTypes[0];
    setPowerVMs((prev) => [...prev, {
      id: Date.now(),
      name: '',
      powerTypeId: firstType?.PowerTypeId ?? '',
      environmentId: ibmPowerEnvironments[0]?.EnvironmentId ?? 1,
      cpu: 0.25,
      ram: 4,
      quantity: 1,
      disks: [],
    }]);
  };

  const handleUpdatePowerVM = (id: number, field: string, value: unknown) => {
    clearSavedCosts();
    setPowerVMs((prev) => prev.map((pvm) => {
      if (pvm.id !== id) return pvm;
      const cleared = { ...pvm, savedLineCost: null };
      if (field === 'cpu') {
        const cpuVal = Number(value);
        return { ...cleared, cpu: cpuVal };
      }
      if (field === 'ram') return { ...cleared, ram: Number(value) };
      if (field === 'quantity') return { ...cleared, quantity: Number(value) || 1 };
      if (field === 'powerTypeId') return { ...cleared, powerTypeId: Number(value) || '' };
      if (field === 'environmentId') return { ...cleared, environmentId: Number(value) || 1 };
      return { ...cleared, [field]: value };
    }));
  };

  const handleDeletePowerVM = (id: number) => { clearSavedCosts(); setPowerVMs((prev) => prev.filter((pvm) => pvm.id !== id)); };

  const handleAddPowerVMDisk = (vmId: number) => {
    clearSavedCosts();
    setPowerVMs((prev) => prev.map((pvm) => (pvm.id !== vmId ? pvm : ((pvm.disks ?? []).length >= 3 ? pvm : { ...pvm, savedLineCost: null, disks: [...(pvm.disks ?? []), filteredPowerDisks[0]?.DiskId ?? null] }))));
  };

  const handleUpdatePowerVMDisk = (vmId: number, idx: number, diskId: number | null) => {
    clearSavedCosts();
    setPowerVMs((prev) => prev.map((pvm) => {
      if (pvm.id !== vmId) return pvm;
      const newDisks = [...(pvm.disks ?? [])]; newDisks[idx] = diskId ?? null;
      return { ...pvm, savedLineCost: null, disks: newDisks };
    }));
  };

  const handleRemovePowerVMDisk = (vmId: number, idx: number) => {
    clearSavedCosts();
    setPowerVMs((prev) => prev.map((pvm) => (pvm.id !== vmId ? pvm : { ...pvm, savedLineCost: null, disks: (pvm.disks ?? []).filter((_, i) => i !== idx) })));
  };

  /* --- Power Addon handlers --- */
  const handleAddPowerAddon = () => {
    const firstRecurring = powerAddons.find(a => !a.IsOneTime);
    if (!firstRecurring) return;
    setPowerAddonItems((prev) => [...prev, { id: Date.now(), powerAddonId: firstRecurring.PowerAddonId, powerAddonOptionId: '', quantity: 1 }]);
  };

  const handleUpdatePowerAddon = (id: number, field: string, value: unknown) => {
    clearSavedCosts();
    setPowerAddonItems((prev) => prev.map((item) => {
      if (item.id !== id) return item;
      if (field === 'powerAddonId') {
        const newAddonId = Number(value) || '';
        const opts = newAddonId ? (powerAddonOptions[newAddonId as number] ?? []) : [];
        const firstOptId = opts.length > 0 ? opts[0]!.PowerAddonOptionId : '';
        return { ...item, savedLineCost: null, powerAddonId: newAddonId, powerAddonOptionId: firstOptId };
      }
      if (field === 'quantity') return { ...item, savedLineCost: null, quantity: Number(value) || 1 };
      return { ...item, savedLineCost: null, [field]: value };
    }));
  };

  const handleDeletePowerAddon = (id: number) => { clearSavedCosts(); setPowerAddonItems((prev) => prev.filter((i) => i.id !== id)); };

  // Auto-sync one-time addons: add when Power VMs are present, remove when none
  useEffect(() => {
    if (isLoadingQuote) return;
    const activeOneTime = powerAddons.filter(a => !!a.IsOneTime && !!a.IsActive);
    if (activeOneTime.length === 0) return;

    if (powerVMs.length === 0) {
      const oneTimeIds = new Set(activeOneTime.map(a => a.PowerAddonId));
      setPowerAddonItems(prev => {
        const filtered = prev.filter(item => !oneTimeIds.has(parseInt(String(item.powerAddonId), 10)));
        return filtered.length === prev.length ? prev : filtered;
      });
      return;
    }

    setPowerAddonItems(prev => {
      const presentIds = new Set(prev.map(item => parseInt(String(item.powerAddonId), 10)));
      const missing = activeOneTime.filter(a => !presentIds.has(a.PowerAddonId));
      if (missing.length === 0) return prev;
      const newRows: PowerAddonLineItem[] = missing.map(a => ({
        id: Date.now() + Math.random(),
        powerAddonId: a.PowerAddonId,
        powerAddonOptionId: '',
        quantity: 1,
      }));
      return [...newRows, ...prev];
    });
  }, [powerVMs.length, powerAddons, isLoadingQuote]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------------- Save ---------------- */
  const buildVmSaveLine = (vm: VMRow) => {
    const vmTypeId = getVMTypeId(vm.cpu, vm.ram);
    const environment = environments.find(e => e.EnvironmentId === (vm.environmentId ?? 1));
    const discountMultiplier = environment?.VMDiscountMultiplier ?? 1.0;
    const diskDiscountMultiplier = environment?.DiskDiscountMultiplier ?? 1.0;

    const baseVMCost = getVMPrice(vmTypeId, vm.osTypeId, selectedRegion);
    const unitVm = baseVMCost * discountMultiplier;

    const diskIds = (vm.disks ?? []).filter(Boolean) as number[];
    const diskUnitCosts = diskIds.map((d) => getDiskPrice(d, selectedRegion));
    const disksUnitCost = diskUnitCosts.reduce((a, b) => a + b, 0) * diskDiscountMultiplier;

    const envDefaultAddons = defaultAddons.filter(da =>
      da.EnvironmentId === (vm.environmentId ?? 1) && da.ApplyToVMs
    );
    const defaultAddonsCost = envDefaultAddons.reduce((sum, da) =>
      sum + (addonPricing[`${da.AddonId}-${selectedRegion}`]?.MonthlyCost ?? 0), 0
    );

    // Backup cost
    const totalDiskGB = diskIds.reduce((s: number, d) => s + getDiskSizeGB(d), 0);
    const backupCostPerGB = getBackupCostPerGB(selectedRegion);
    const backupCost = totalDiskGB * backupCostPerGB * Math.max(0, (vm.dailyBackups ?? 0) - 1);
    const backupUnitCost = totalDiskGB * backupCostPerGB;

    // DR testing cost
    const drTestingCost = getDRTestingCost(vm.drTestingFrequency ?? 0, selectedRegion);

    const unitCost = unitVm;
    const lineTotalMonthlyCost = (unitCost + defaultAddonsCost + disksUnitCost + backupCost + drTestingCost) * vm.quantity;

    return {
      vmTypeId, osTypeId: parseInt(String(vm.osTypeId), 10),
      environmentId: vm.environmentId ?? 1,
      name: vm.name ?? 'Virtual Machine', quantity: vm.quantity, backups: vm.dailyBackups ?? 0,
      drTestingFrequency: vm.drTestingFrequency ?? 0,
      backupUnitCost, backupCost,
      unitCost, defaultAddonsCost, disksUnitCost, lineTotalMonthlyCost,
      disks: diskIds, diskUnitCosts, appliedDefaultAddons: [] as unknown[],
    };
  };

  const buildPaasDbSaveLine = (db: PaasDatabaseRow) => {
    const unitCost = getDatabasePrice(db.databaseConfigId, selectedRegion);
    const backupCostPerGB = getDatabaseBackupCostPerGB(selectedRegion);
    const extraStorageCostPerGB = getDatabaseExtraStorageCostPerGB(selectedRegion);
    const extraStorageCost = (db.extraStorageGB ?? 0) * extraStorageCostPerGB;
    const backupCost = (db.extraStorageGB ?? 0) * backupCostPerGB * Math.max(0, (db.dailyBackups ?? 0) - 1);
    const lineTotalMonthlyCost = (unitCost + extraStorageCost + backupCost) * db.quantity;
    return {
      databaseConfigId: parseInt(String(db.databaseConfigId), 10),
      quantity: db.quantity,
      backups: db.dailyBackups ?? 0,
      extraStorageGB: db.extraStorageGB ?? 0,
      unitCost,
      extraStorageCost,
      backupCost,
      lineTotalMonthlyCost,
    };
  };

  const buildDbVmSaveLine = (db: VmDatabaseRow) => {
    const vmTypeId = getVMTypeId(db.cpu, db.ram);
    const environment = environments.find(e => e.EnvironmentId === (db.environmentId ?? 1));
    const discountMultiplier = environment?.DatabaseVMDiscountMultiplier ?? 1.0;
    const diskDiscountMultiplier = environment?.DiskDiscountMultiplier ?? 1.0;

    const baseCost = databaseVMPricing[`${vmTypeId}-${parseInt(String(db.editionId), 10)}-${selectedRegion}`]?.MonthlyCost ?? 0;
    const base = baseCost * discountMultiplier;

    const diskIds = (db.disks ?? []).filter(Boolean) as number[];
    const diskUnitCosts = diskIds.map((d) => getDiskPrice(d, selectedRegion));
    const disksUnitCost = diskUnitCosts.reduce((a, b) => a + b, 0) * diskDiscountMultiplier;

    const envDefaultAddons = defaultAddons.filter(da =>
      da.EnvironmentId === (db.environmentId ?? 1) && da.ApplyToDatabaseVMs
    );
    const defaultAddonsCost = envDefaultAddons.reduce((sum, da) =>
      sum + (addonPricing[`${da.AddonId}-${selectedRegion}`]?.MonthlyCost ?? 0), 0
    );

    // Backup cost
    const totalDiskGB = diskIds.reduce((s: number, d) => s + getDiskSizeGB(d), 0);
    const backupCostPerGB = getBackupCostPerGB(selectedRegion);
    const backupCost = totalDiskGB * backupCostPerGB * Math.max(0, (db.dailyBackups ?? 0) - 1);

    const unitCost = base;
    const lineTotalMonthlyCost = (unitCost + defaultAddonsCost + disksUnitCost + backupCost) * db.quantity;

    return {
      vmTypeId, databaseEditionId: parseInt(String(db.editionId), 10),
      environmentId: db.environmentId ?? 1,
      quantity: db.quantity, backups: db.dailyBackups ?? 0,
      unitCost, defaultAddonsCost, disksUnitCost, lineTotalMonthlyCost,
      disks: diskIds, diskUnitCosts, appliedDefaultAddons: [] as unknown[],
    };
  };

  const handleSaveQuote = async () => {
    if (!quoteName.trim()) { setError('Please enter a quote name'); return; }
    if (!selectedProvider || !selectedRegion) { setError('Please select provider and region'); return; }
    if (vms.length === 0 && databases.length === 0 && otherLineItems.length === 0 && powerVMs.length === 0 && powerAddonItems.length === 0) { setError('Please add at least one item'); return; }

    setSuccessMsg('');
    try {
      const vmsData = vms.map(buildVmSaveLine);
      const databasesData = databases.filter((db): db is PaasDatabaseRow => db.deploymentType === 'paas').map((db) => buildPaasDbSaveLine({ ...db }));
      const databaseVMsData = databases.filter((db): db is VmDatabaseRow => db.deploymentType === 'vm_based').map(buildDbVmSaveLine);
      const addonsData = otherLineItems
        .filter((item) => item.itemId && !isNaN(parseInt(String(item.itemId), 10)))
        .map((item) => {
          const addonId = parseInt(String(item.itemId), 10);
          const optionId = item.optionId ? parseInt(String(item.optionId), 10) : null;
          const unitOpt = getAddonCostByIds(addonId, optionId);
          return { addonId, addonOptionId: optionId, quantity: item.quantity, unitCost: unitOpt, lineTotalMonthlyCost: unitOpt * item.quantity };
        });

      // Power VM save data
      const powerVMsData = powerVMs.map((pvm) => {
        const pvmKey = `${pvm.powerTypeId}-${selectedRegion}`;
        const pvmPricing = powerVMPricing[pvmKey];
        const environment = environments.find(e => e.EnvironmentId === (pvm.environmentId ?? 1));
        const discountMultiplier = environment?.VMDiscountMultiplier ?? 1.0;
        const diskDiscountMultiplier = environment?.DiskDiscountMultiplier ?? 1.0;

        const cpuUnitCost = pvmPricing?.CostPerQuarterCore ?? 0;
        const ramUnitCost = pvmPricing?.CostPerGBRam ?? 0;
        const cpuCost = (pvm.cpu / 0.25) * cpuUnitCost;
        const ramCost = pvm.ram * ramUnitCost;
        const baseCost = (cpuCost + ramCost) * discountMultiplier;

        const diskIds = (pvm.disks ?? []).filter(Boolean) as number[];
        const diskUnitCosts = diskIds.map((d) => getPowerDiskPrice(d, selectedRegion));
        const disksUnitCost = diskUnitCosts.reduce((a, b) => a + b, 0) * diskDiscountMultiplier;

        const envPDA = powerDefaultAddons.filter(da => da.EnvironmentId === (pvm.environmentId ?? 1));
        const defaultAddonsCost = envPDA.reduce((sum, da) =>
          sum + (powerAddonPricing[`${da.PowerAddonId}-${selectedRegion}`]?.MonthlyCost ?? 0), 0);

        const lineTotalMonthlyCost = (baseCost + defaultAddonsCost + disksUnitCost) * pvm.quantity;

        return {
          powerTypeId: parseInt(String(pvm.powerTypeId), 10),
          environmentId: pvm.environmentId ?? 1,
          name: pvm.name ?? '',
          cpu: pvm.cpu, ram: pvm.ram, quantity: pvm.quantity,
          cpuUnitCost, ramUnitCost, defaultAddonsCost, disksUnitCost,
          lineTotalMonthlyCost, disks: diskIds, diskUnitCosts,
          appliedDefaultAddons: envPDA.map(da => ({
            powerAddonId: da.PowerAddonId,
            unitCost: powerAddonPricing[`${da.PowerAddonId}-${selectedRegion}`]?.MonthlyCost ?? 0,
          })),
        };
      });

      const powerAddonsData = powerAddonItems
        .filter((item) => item.powerAddonId && !isNaN(parseInt(String(item.powerAddonId), 10)))
        .map((item) => {
          const addonId = parseInt(String(item.powerAddonId), 10);
          const optId = item.powerAddonOptionId ? parseInt(String(item.powerAddonOptionId), 10) : null;
          const unitCost = getPowerAddonCostByIds(addonId, optId);
          return { powerAddonId: addonId, powerAddonOptionId: optId, quantity: item.quantity, unitCost, lineTotalMonthlyCost: unitCost * item.quantity };
        });

      const isOverwrite = loadedQuoteId && quoteName.trim() === loadedQuoteName.trim();

      const payload = {
        quoteId: isOverwrite ? loadedQuoteId : undefined,
        quoteName,
        providerId: parseInt(String(selectedProvider), 10),
        regionId: parseInt(String(selectedRegion), 10),
        totalMonthlyCost: totals.monthly,
        totalAnnualCost: totals.annual,
        vms: vmsData, databases: databasesData, databaseVMs: databaseVMsData, addons: addonsData,
        powerVMs: powerVMsData, powerAddons: powerAddonsData,
      };

      const resp = await quotesAPI.save(payload);

      const savedId = resp?.data?.quoteId ?? loadedQuoteId;
      if (savedId) {
        setLoadedQuoteId(savedId);
        setLoadedQuoteName(quoteName.trim());
        window.history.replaceState(null, '', `/quote-builder?load=${savedId}`);
      }

      setSuccessMsg(isOverwrite ? 'Quote updated successfully!' : 'Quote saved successfully!');
    } catch (e) {
      console.error('Save failed', e);
      setError('Failed to save quote');
    }
  };

  /* ---------------- PDF Export ---------------- */
  const handleExportPDF = async () => {
    await generatePDF({
      logo, quoteName, providers, regions, environments, osTypes, disks,
      databaseTypes, databaseEditions, addons, addonOptions,
      selectedProvider, selectedRegion, loadedQuoteId, username: user?.username ?? '\u2014',
      vms, databases, otherLineItems, totals,
      calculateVMCost, getVMTypeId, getDatabaseVMPrice, getDatabasePrice, getDatabaseTotalPrice,
      getDiskSizeGB, getBackupCostPerGB, getAddonCostByIds,
      isVMRowAvailable, isPaasDbRowAvailable, isVmDbRowAvailable, isAddonRowAvailable,
      powerVMs, powerTypes, powerAddons, powerAddonItems,
      calculatePowerVMCost, getPowerAddonCost,
      isPowerVMRowAvailable, isPowerAddonRowAvailable,
    });
  };

  /* ---------------- Excel Export ---------------- */
  const handleExportExcel = async () => {
    const { generateExcel } = await import('./exportExcel');
    await generateExcel({
      quoteName, providers, regions, environments, osTypes, disks,
      databaseTypes, databaseEditions, addons, addonOptions,
      selectedProvider, selectedRegion, loadedQuoteId, username: user?.username ?? '\u2014',
      vms, databases, otherLineItems, totals,
      calculateVMCost, getVMTypeId, getDatabaseVMPrice, getDatabasePrice, getDatabaseTotalPrice,
      getDiskSizeGB, getBackupCostPerGB, getAddonCostByIds,
      isVMRowAvailable, isPaasDbRowAvailable, isVmDbRowAvailable, isAddonRowAvailable,
      powerVMs, powerTypes, powerAddons, powerAddonItems,
      calculatePowerVMCost, getPowerAddonCost,
      isPowerVMRowAvailable, isPowerAddonRowAvailable,
    });
  };

  /* ---------------- Render ---------------- */
  if (loading) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  const cpuOptions: SelectOption[] = [...new Set(filteredVMTypes.map((v) => v.CPU))].sort((a, b) => a - b).map((cpu) => ({ value: cpu, label: cpu }));

  return (
    <>
      <AppHeader />

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
        {successMsg && <Alert severity="success" onClose={() => setSuccessMsg('')} sx={{ mb: 2 }}>{successMsg}</Alert>}
        {bannerMsg && <Alert severity="warning" onClose={() => setBannerMsg('')} sx={{ mb: 2 }}>{bannerMsg}</Alert>}

        <QuoteHeader
          providers={providers}
          regions={regions}
          selectedProvider={selectedProvider}
          selectedRegion={selectedRegion}
          quoteName={quoteName}
          totals={totals}
          onProviderChange={(v) => { clearSavedCosts(); setSelectedProvider(v); setPowerVMs([]); setPowerAddonItems([]); }}
          onRegionChange={(v) => { clearSavedCosts(); setSelectedRegion(v); }}
          onQuoteNameChange={setQuoteName}
          onSave={handleSaveQuote}
          onExportPDF={handleExportPDF}
          onExportExcel={handleExportExcel}
          onAddVM={handleAddVM}
          onAddDatabase={handleAddDatabase}
          onAddOtherItem={handleAddOtherItem}
          onAddPowerVM={handleAddPowerVM}
          onAddPowerAddon={powerAddons.some(a => !a.IsOneTime) ? handleAddPowerAddon : undefined}
          showAddButtons={!!(selectedProvider && selectedRegion)}
          isIBM={isIBM}
        />

        {selectedProvider && selectedRegion && (
          <>
            <VMsTable
              vms={vms}
              environments={standardEnvironments}
              filteredOSTypes={filteredOSTypes}
              osTypes={osTypes}
              filteredDisks={filteredDisks}
              disks={disks}
              addons={addons}
              defaultAddons={defaultAddons}
              pricingReady={pricingReady}
              onUpdateVM={handleUpdateVM}
              onDeleteVM={handleDeleteVM}
              onAddDisk={handleAddDisk}
              onUpdateDisk={handleUpdateDisk}
              onRemoveDisk={handleRemoveDisk}
              isVMRowAvailable={isVMRowAvailable}
              calculateVMCost={calculateVMCost}
              ensureCurrentOption={ensureCurrentOption}
              getAvailableRAM={getAvailableRAM}
              cpuOptions={cpuOptions}
            />
            <VMConfigSection vms={vms} onUpdateVM={handleUpdateVM} />

            <DatabasesTable
              databases={databases}
              environments={standardEnvironments}
              filteredVMTypes={filteredVMTypes}
              filteredDisks={filteredDisks}
              disks={disks}
              databaseTypes={databaseTypes}
              filteredDatabaseTypes={filteredDatabaseTypes}
              databaseEditions={databaseEditions}
              filteredDatabaseConfigs={filteredDatabaseConfigs}
              addons={addons}
              defaultAddons={defaultAddons}
              databaseVMPricing={databaseVMPricing}
              selectedRegion={selectedRegion}
              pricingReady={pricingReady}
              onUpdateDatabase={handleUpdateDatabase}
              onDeleteDatabase={handleDeleteDatabase}
              onAddDatabaseDisk={handleAddDatabaseDisk}
              onUpdateDatabaseDisk={handleUpdateDatabaseDisk}
              onDeleteDatabaseDisk={handleDeleteDatabaseDisk}
              isVmDbRowAvailable={isVmDbRowAvailable}
              isPaasDbRowAvailable={isPaasDbRowAvailable}
              getVMTypeId={getVMTypeId}
              getDatabaseVMPrice={getDatabaseVMPrice}
              getDatabasePrice={getDatabasePrice}
              getAvailableRAMForDatabase={getAvailableRAMForDatabase}
              getAvailableTiers={getAvailableTiers}
              ensureCurrentOption={ensureCurrentOption}
            />

            <OtherItemsTable
              otherLineItems={otherLineItems}
              addons={addons}
              filteredAddons={filteredAddons}
              addonOptions={addonOptions}
              addonOptionPricing={addonOptionPricing}
              defaultAddons={defaultAddons}
              selectedRegion={selectedRegion}
              pricingReady={pricingReady}
              onUpdateOtherItem={handleUpdateOtherItem}
              onDeleteOtherItem={handleDeleteOtherItem}
              isAddonRowAvailable={isAddonRowAvailable}
              getAddonCostByIds={getAddonCostByIds}
              ensureCurrentOption={ensureCurrentOption}
            />

            {isIBM && (
              <>
                <PowerVMsTable
                  powerVMs={powerVMs}
                  environments={ibmPowerEnvironments}
                  powerTypes={powerTypes}
                  powerAddons={powerAddons}
                  filteredDisks={filteredPowerDisks}
                  disks={disks}
                  powerDefaultAddons={powerDefaultAddons}
                  pricingReady={pricingReady}
                  onUpdatePowerVM={handleUpdatePowerVM}
                  onDeletePowerVM={handleDeletePowerVM}
                  onAddPowerVMDisk={handleAddPowerVMDisk}
                  onUpdatePowerVMDisk={handleUpdatePowerVMDisk}
                  onRemovePowerVMDisk={handleRemovePowerVMDisk}
                  isPowerVMRowAvailable={isPowerVMRowAvailable}
                  calculatePowerVMCost={calculatePowerVMCost}
                />

                <PowerAddonsTable
                  powerAddonItems={powerAddonItems}
                  powerAddons={powerAddons}
                  powerAddonOptions={powerAddonOptions}
                  powerAddonOptionPricing={powerAddonOptionPricing}
                  powerDefaultAddons={powerDefaultAddons}
                  selectedRegion={selectedRegion}
                  pricingReady={pricingReady}
                  onUpdatePowerAddon={handleUpdatePowerAddon}
                  onDeletePowerAddon={handleDeletePowerAddon}
                  isPowerAddonRowAvailable={isPowerAddonRowAvailable}
                  getPowerAddonCostByIds={getPowerAddonCostByIds}
                  ensureCurrentOption={ensureCurrentOption}
                />
              </>
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default QuoteBuilder;
